import {
  TravelMode, TravelPhase,
  type TravelHex, type TravelRoute,
} from './TravelTypes';

/**
 * Pure state container + FSM for the travel system.
 * No EventBus, no side-effects — TravelSystem reads this and emits events.
 */
export class TravelState {
  phase = TravelPhase.Idle;
  mode = TravelMode.Normal;

  startHex: TravelHex = { q: 0, r: 0 };
  endHex: TravelHex = { q: 0, r: 0 };
  hasStart = false;
  hasEnd = false;

  route: TravelRoute | null = null;
  elapsedSeconds = 0;
  currentHopIdx = 0;

  private _prevPhase = TravelPhase.Idle;

  get phaseChanged(): boolean {
    return this.phase !== this._prevPhase;
  }

  ackPhaseChange(): void {
    this._prevPhase = this.phase;
  }

  beginSelectStart(): boolean {
    if (this.phase !== TravelPhase.Idle && this.phase !== TravelPhase.Arrived) return false;
    this.phase = TravelPhase.SelectingStart;
    return true;
  }

  beginSelectEnd(): boolean {
    if (this.phase !== TravelPhase.SelectingStart && this.phase !== TravelPhase.Idle
      && this.phase !== TravelPhase.Arrived) return false;
    if (!this.hasStart) return false;
    this.phase = TravelPhase.SelectingEnd;
    return true;
  }

  setStart(q: number, r: number): void {
    this.startHex.q = q;
    this.startHex.r = r;
    this.hasStart = true;
  }

  setEnd(q: number, r: number): void {
    this.endHex.q = q;
    this.endHex.r = r;
    this.hasEnd = true;
  }

  beginPathfinding(mode: TravelMode): boolean {
    if (!this.hasStart || !this.hasEnd) return false;
    this.mode = mode;
    this.phase = TravelPhase.Pathfinding;
    this.route = null;
    this.elapsedSeconds = 0;
    this.currentHopIdx = 0;
    return true;
  }

  setRoute(route: TravelRoute): void {
    this.route = route;
    this.phase = TravelPhase.Traveling;
  }

  arrive(): void {
    this.phase = TravelPhase.Arrived;
  }

  cancel(): void {
    this.phase = TravelPhase.Idle;
    this.route = null;
    this.elapsedSeconds = 0;
    this.currentHopIdx = 0;
  }

  reset(): void {
    this.cancel();
    this.hasStart = false;
    this.hasEnd = false;
    this.startHex.q = 0;
    this.startHex.r = 0;
    this.endHex.q = 0;
    this.endHex.r = 0;
  }
}
